import React, { useCallback, useMemo } from "react";
import CrudForm from "../shared/crud/form";
import { usePropertyLocales } from "../../components/property-locale-provider";
import actions from "../../lib/actions";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { v4 as uuidv4 } from "uuid";
import { PropertyEntity } from "./domain/property.entity";
import { useProperty } from "../../components/property-provider";
import { getEntity, updateEntity } from "./infrastructure/property.api";
import { getDataByActions, Image } from "../shared/widgets/ImageManager";
import { useConfig } from "../../lib/config";
import { useIntl } from "react-intl";
import queryString from "query-string";
import { useHttpClient } from "../../lib/http";
import { BACKOFFICE_MANAGER_URL } from "../witadmin/request";
import { Multimedia } from "../../gen/witbooking/backoffice/common/common_pb";
import { FormValidation, IChangeEvent } from "react-jsonschema-form";
import validate from "validate.js";
import styled from "styled-components";
import { schema } from "./infrastructure/property.jsonschema";
import { messages } from "./messages";
import { buildFormConfiguration } from "./domain/property.formConfiguration";
import { usePropertyConfiguration } from "../../components/property-configuration-provider";

export const FormContainer = styled("div")`
  .GooglePlacesSelector {
    width: 500px;
  }
  .help-block {
    color: gray;
    font-size: 12px;
    margin-top: 4px;
    margin-left: 2px;
  }

  #root_propertyType,
  #root_valuation {
    width: 350px;
  }
`;

function isValidLatitude(latitude: number): boolean {
  return latitude >= -90 && latitude <= 90;
}

function isValidLongitude(longitude: number): boolean {
  return longitude >= -180 && longitude <= 180;
}
const constraints = {
  email: {
    email: true,
  },
};

function PropertyCRUD({ propertyTicker }: { propertyTicker: string }) {
  const intl = useIntl();
  const allowedLocales = usePropertyLocales();
  const propertyInfo = useProperty();
  const config = useConfig();
  const propertyConfiguration = usePropertyConfiguration();
  const httpClient = useHttpClient(BACKOFFICE_MANAGER_URL);
  const onGetOne = useCallback(async () => getEntity(propertyInfo.token), [propertyInfo.token]);

  const fetchCreateImages = useCallback(
    async (propertyTicker: string, entityId: string, data: Image[] = []) => {
      if (data.length) {
        const formData = new FormData();
        data.forEach(d => {
          formData.append("images", d, d.id);
          formData.append(
            "medias",
            new Blob(
              [
                JSON.stringify({
                  id: d.id,
                  filename: d.filename.toLowerCase(),
                  translations: [],
                }),
              ],
              {
                type: "application/json",
              }
            )
          );
        });
        await httpClient.post<FormData, Multimedia.AsObject>(
          `/v1/${propertyTicker}/property/${entityId}/media/multi`,
          formData
        );
      }
    },
    [httpClient]
  );

  const fetchRemoveImages = useCallback(
    async (propertyTicker: string, entityId: string, data: Image[]) => {
      if (data.length) {
        const request = data.map(d => d.id);
        await httpClient.delete<any>(
          `/v1/${propertyTicker}/property/${entityId}/media?${queryString.stringify({
            mediaIds: request,
          })}`
        );
      }
    },
    [httpClient]
  );

  const fetchUpdateImages = useCallback(
    async (propertyTicker: string, entityId: string, data: Image[]) => {
      if (data.length) {
        const request = data.map(d => ({
          ...d,
          translations: [],
        }));
        await httpClient.put<any, Multimedia.AsObject>(
          `/v1/${propertyTicker}/property/${entityId}/media`,
          request
        );
      }
    },
    [httpClient]
  );

  const uploadImages = useCallback(
    async ({
      propertyTicker,
      id,
      multimediaList,
      currentMultimediaList,
    }: {
      propertyTicker: string;
      id: string;
      multimediaList: Image[];
      currentMultimediaList: Image[];
    }) => {
      const { create, remove } = getDataByActions(currentMultimediaList, multimediaList);
      // first apply the mutations to the entities to persist them
      await Promise.all([
        fetchCreateImages(propertyTicker, id, create),
        fetchRemoveImages(propertyTicker, id, remove),
      ]);
      // fetch the entities to get the definitive id's
      const propertyEntity = await getEntity(propertyInfo.token);
      // keep the order of the images by following the order of the multimediaList which is the one given by the widget
      // for new images in the multimediaList, which don't have definite id's  yet, we will use the filename to match them
      const orderedMultimedia = multimediaList
        .map(m => {
          return propertyEntity.multimediaList.find(
            it => it.id === m.id || it.filename.includes(m.id)
          );
        })
        .filter((it): it is Image => Boolean(it));
      // reorder the property images to match the multimediaList (widget's) order
      return fetchUpdateImages(propertyTicker, id, orderedMultimedia);
    },
    [fetchCreateImages, fetchRemoveImages, propertyInfo.token, fetchUpdateImages]
  );
  const onUpdate: (entity: PropertyEntity) => Promise<void> = useCallback(
    async entity => {
      const propertyEntity = await getEntity(propertyInfo.token);
      await updateEntity(entity, propertyInfo.token);
      await uploadImages({
        propertyTicker,
        id: uuidv4(),
        multimediaList: entity.multimediaList,
        currentMultimediaList: propertyEntity.multimediaList,
      });
      alert(intl.formatMessage(messages.saveSuccess));
      return;
    },
    [intl, propertyInfo.token, propertyTicker, uploadImages]
  );

  const formConfiguration = useMemo(
    () => buildFormConfiguration(allowedLocales, config, propertyConfiguration),
    [allowedLocales, config, propertyConfiguration]
  );

  const validateFunction = useCallback(
    (formData: PropertyEntity, errors: FormValidation) => {
      if (!isValidLatitude(formData.latitude)) {
        errors.latitude.addError(intl.formatMessage(messages.invalidCoordinate));
      }
      if (!isValidLongitude(formData.longitude)) {
        errors.longitude.addError(intl.formatMessage(messages.invalidCoordinate));
      }
      if (!formData.multimediaList || formData.multimediaList.length < 1) {
        errors.multimediaList.addError(intl.formatMessage(messages.requiredImages));
      }
      if (validate({ email: formData.adminEmail }, constraints)) {
        errors.adminEmail.addError(intl.formatMessage(messages.emailValidationFailed));
      }
      if (validate({ email: formData.contactEmail }, constraints)) {
        errors.contactEmail.addError(intl.formatMessage(messages.emailValidationFailed));
      }
      return errors;
    },
    [intl]
  );

  const handleOnChange = useCallback((e: IChangeEvent<PropertyEntity>) => {
    if (
      e.formData.searchCoordinates &&
      e.formData.searchCoordinates.latitude &&
      e.formData.searchCoordinates.longitude
    ) {
      const newData = { ...e.formData };
      newData.latitude = e.formData.searchCoordinates.latitude;
      newData.longitude = e.formData.searchCoordinates.longitude;
      newData.timeZone = e.formData.searchCoordinates.timeZone;
      return newData;
    }
    return e.formData;
  }, []);

  if (allowedLocales === undefined) {
    return null;
  }

  return (
    <CrudForm<PropertyEntity>
      id={"property"}
      schema={schema}
      formConfiguration={formConfiguration}
      messages={messages}
      onGet={onGetOne}
      onUpdate={onUpdate}
      canUpdate={actions.ModifyPropertyInfo}
      validate={validateFunction}
      container={FormContainer}
      enumsOptional
      customOnChange={handleOnChange}
    />
  );
}

export default withAuthorizationVerifier(PropertyCRUD);
